

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getAnalytics, logEvent } from "firebase/analytics";

// Lightbox imports
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

// Lightbox styles
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// Local imports
import './Art.scss';
import Menu from '../../components/menu/Menu';
import Splash from '../splash/Splash';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBorderAll, faLayerGroup, faList } from '@fortawesome/free-solid-svg-icons';

// Dynamic loading function with error handling
const loadDrawingsFromFolder = async () => {
  try {
    const response = await fetch('/drawing/manifest.json');
    const manifest = await response.json();
    return manifest.images.map((filename, index) => ({
      src: `/drawing/${filename}`,
      title: `Artwork ${index + 1}`,
      description: filename.replace(/\.[^/.]+$/, "").replace(/[-_]/g, ' ')
    }));
  } catch (error) {
    console.warn('Could not load manifest.json, falling back to static list');
    return [];
  }
};

// Enhanced ArtPiece component
const ArtPiece = React.memo(({ src, onClick, index, isVisible }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleClick = useCallback(() => {
    if (isLoaded && !imageError) {
      onClick(index);
    }
  }, [onClick, index, isLoaded, imageError]);

  const handleKeyDown = useCallback((e) => {
    if ((e.key === 'Enter' || e.key === ' ') && isLoaded && !imageError) {
      e.preventDefault();
      onClick(index);
    }
  }, [onClick, index, isLoaded, imageError]);

  return (
    <li 
      className={`art-piece ${isLoaded ? 'loaded' : ''} ${isVisible ? 'visible' : ''}`}
      style={{ '--delay': `${index * 0.1}s` }}
    >
      <div className="art-piece-inner"
      
        onClick={handleClick}
      >
        {!imageError ? (
          <>
            {/* Skeleton loader */}
            <div className={`skeleton-loader ${isLoaded ? 'hidden' : ''}`} />
            
            {/* Actual image */}
            {/* <img 
              src={isVisible ? src : undefined}
              alt={`Artwork ${index + 1}`} 
              onClick={handleClick}
              onLoad={handleImageLoad}
              onError={handleImageError}
              role="button"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              className={`art-image ${isLoaded ? 'loaded' : ''}`}
              loading="lazy"
            /> */}


            <img 
              src={src}
              alt={`Artwork ${index + 1}`} 
            
              onLoad={handleImageLoad}
              onError={handleImageError}
              role="button"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              className={`art-image ${isLoaded ? 'loaded' : ''} ${isVisible ? 'visible' : ''}`}
              loading="lazy"
            />
            
            {/* Hover overlay with effects */}
            <div className="art-overlay">
              <div className="art-overlay-content">
                <div className="view-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <span className="art-index">#{String(index + 1).padStart(2, '0')}</span>
              </div>
              <div className="animated-border">
                <div className="border-line top"></div>
                <div className="border-line right"></div>
                <div className="border-line bottom"></div>
                <div className="border-line left"></div>
              </div>
            </div>
          </>
        ) : (
          <div className="error-placeholder">
            <div className="error-icon">⚠</div>
            <span>Failed to load image</span>
          </div>
        )}
      </div>
    </li>
  );
});

// Intersection Observer hook
const useIntersectionObserver = (options) => {
  const [elements, setElements] = useState([]);
  const [entries, setEntries] = useState([]);
  const observer = useRef();

  const observe = useCallback((element) => {
    if (element && !elements.includes(element)) {
      setElements(prev => [...prev, element]);
    }
  }, [elements]);

  useEffect(() => {
    if (elements.length > 0) {
      observer.current = new IntersectionObserver((observerEntries) => {
        setEntries(observerEntries);
      }, options);

      elements.forEach(element => observer.current.observe(element));

      return () => observer.current?.disconnect();
    }
  }, [elements, options]);

  return { observe, entries };
};

export default function Art() {
  const [lightboxState, setLightboxState] = useState({
    photoIndex: 0,
    isOpen: false
  });
  const [showSplash, setShowSplash] = useState(true);
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleImages, setVisibleImages] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");


  const [layout, setLayout] = useState("masonry"); // options: "masonry", "compact", "spacious"



  const galleryRef = useRef(null);
  const { observe, entries } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '50px'
  });

  // Show images progressively
  useEffect(() => {
    entries.forEach(entry => {
      const index = parseInt(entry.target.dataset.index);
      if (entry.isIntersecting) {
        setVisibleImages(prev => new Set(prev).add(index));
      }
    });
  }, [entries]);

  const openLightbox = useCallback((index) => {
    setLightboxState({
      photoIndex: index,
      isOpen: true
    });
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'artwork_viewed', { artwork_index: index });
    } catch (error) {
      console.warn('Analytics not available');
    }
  }, []);


  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'art_page_visited');
      } catch (error) {
        console.warn('Analytics not available');
      }
      const drawingsList = await loadDrawingsFromFolder();
      console.log(drawingsList);
      setDrawings(drawingsList);
      setLoading(false);

      const timer = setTimeout(() => setShowSplash(false), 1000);
      return () => clearTimeout(timer);
    };
    initializeComponent();
  }, []);

  useEffect(() => {
    if (!loading && galleryRef.current) {
      const galleryItems = galleryRef.current.querySelectorAll('.art-piece');
      galleryItems.forEach((item, index) => {
        item.dataset.index = index;
        observe(item);
      });
    }
  }, [loading, drawings, observe]);

  const filteredDrawings = drawings.filter(art =>
    art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    art.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (showSplash) return <Splash />;

  if (loading) {
    return (
      <div className="home art-main">
        <Menu detail={true} />
        <div className="artwork-container">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p>Loading artwork...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Menu detail={true} />
      <div className="home art-main">
        <div className="bg-effects">
          <div className="grid-overlay"></div>
          <div className="gradient-orbs">
            <div className="orb orb-1"></div>
            <div className="orb orb-2"></div>
            <div className="orb orb-3"></div>
          </div>
        </div>

        <div className="artwork-container">
          <div className="gallery-header">
            <h1 className="gallery-title">
              <span className="title-main">Digital Gallery</span>
              {/* <span className="title-count">{filteredDrawings.length} Artworks</span> */}
            </h1>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search artworks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

               <div class="layout-options">
    <button className="layout-btn active" data-layout="grid"
      onClick={() => setLayout("masonry")}
    >


      <FontAwesomeIcon icon={faBorderAll} className="nav_icon" />
      
    </button>
    <button className="layout-btn" data-layout="list"
      onClick={() => setLayout("spacious")}
    >

        <FontAwesomeIcon icon={faList} className="nav_icon" />
    </button>
    <button className="layout-btn" data-layout="compact"
      onClick={() => setLayout("compact")}
    >
     
        <FontAwesomeIcon icon={faLayerGroup} className="nav_icon" />
    </button>
  </div>
            </div>

             


          </div>


        {/* <div className="layout-options">
          <button onClick={() => setLayout("masonry")}>🧱 Masonry</button>
          <button onClick={() => setLayout("compact")}>📐 Compact</button>
          <button onClick={() => setLayout("spacious")}>🖼 Spacious</button>
        </div> */}

          <div className="gallery-wrapper">

         <ul className={`gallery ${layout}`} ref={galleryRef}>
              {filteredDrawings.map((art, index) => (
                <ArtPiece 
                  key={`artwork-${index}`}
                  src={art.src} 
                  onClick={openLightbox} 
                  index={index}
                  isVisible={visibleImages.has(index)}
                />
              ))}
            </ul>
          </div>

          {/* {lightboxState.isOpen && ( */}
            <Lightbox
              open={lightboxState.isOpen}
              // onClose={closeLightbox}
              close={() => setLightboxState({ isOpen: false, photoIndex: 0 })}
              slides={filteredDrawings}
              plugins={[Captions, Fullscreen, Thumbnails, Zoom]}
              index={lightboxState.photoIndex}
              carousel={{ finite: true }}
              animation={{ fade: 300 }}
            />
          {/* )} */}
        </div>
      </div>
    </>
  );
}
